import classNames from "classnames";
import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";

import { PWSSessionBackend, PwsGiftRecipientResponse, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import Spinner from "../../../../../core/ui/Spinner.component";
import { axiosErrorReason } from "../../../lib/coastCommon";
import { emailPattern, FORM_ERRORS, SelectInput, SelectOption, SimpleInput } from "../../forms";
import { RecaptchaForm, RecaptchaFromOnSubmit } from "../../forms/RecaptchaForm.component";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("CoastGiftRecipient.component");

interface CoastGiftRecipientProps {
  session: PWSSessionBackend;
  goForward: () => void;
  onCancel: () => void;
  goBack: () => void;
  stepNumber: number;
  stepCount: number;
  hidden: boolean;
  store?: Store;
}

export interface CoastGiftRecipientInputs {
  country: string;
  email: string;
  name: string;
}

export const CoastGiftRecipient = inject("store")(
  ({ onCancel, goForward, hidden, goBack, session, stepNumber, stepCount, store }: CoastGiftRecipientProps) => {
    const { register, errors } = useFormContext<CoastGiftRecipientInputs>();
    const [isProcessing, setIsProcessing] = useState(false);
    const [formError, setFormError] = useState<string>();

    const preSubmit = useCallback(() => {
      if (formError) {
        setFormError("");
      }
      setIsProcessing(true);
    }, [formError]);

    const onSubmit: RecaptchaFromOnSubmit<CoastGiftRecipientInputs> = useCallback(
      async ({ email }, token) => {
        if (!session.state.accessToken) {
          log.error("Error processing request: missing accessToken");
          setFormError(COAST_MESSAGES.MISSING_ACCESS_TOKEN);
          setIsProcessing(false);
          return;
        }

        try {
          const result: PwsGiftRecipientResponse = await session.memberService.ensureGiftReceiverAccount(
            email,
            token,
            session.state.accessToken,
          );

          if (result.userId) {
            session.giftRecipient = result;
            goForward();
          } else {
            setFormError(COAST_MESSAGES.SUBSCRIPTION_RECIPIENT_ERROR);
          }
        } catch (e) {
          setFormError(axiosErrorReason(e) || COAST_MESSAGES.UNEXPECTED_ERROR);
          setIsProcessing(false);
        } finally {
          setIsProcessing(false);
        }
      },
      [goForward, session],
    );

    if (!store) {
      return null;
    }

    return (
      <section className={classNames("component-pick-your-plan", "sign-up-step", { hidden })}>
        <p className="step-indicator">
          Step {stepNumber} of {stepCount}
        </p>

        <Heading level={2} className="section-title">
          Enter Your Gift Recipient&apos;s Info
        </Heading>

        <RecaptchaForm
          siteKey={store.env.COAST_RECAPTCHA_V3_PUBLIC_KEY}
          setIsProcessing={setIsProcessing}
          action="giftee_account"
          setError={setFormError}
          preSubmit={preSubmit}
          onSubmit={onSubmit}
        >
          <SelectInput
            register={register}
            rules={{ required: FORM_ERRORS.required }}
            error={errors.country}
            placeholder={{ label: "Select recipient country...", value: "" }}
            label="Recipient Location"
            name="country"
          >
            <SelectOption label="United States" value="US" />
            <SelectOption label="Canada" value="CA" />
          </SelectInput>

          <SimpleInput
            placeholder="Enter recipient name"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.name}
            register={register}
            label="Recipient Name"
            name="name"
            type="text"
          />

          <SimpleInput
            placeholder="Enter recipient email"
            rules={{
              required: FORM_ERRORS.required,
              pattern: emailPattern,
            }}
            error={errors.email}
            register={register}
            label="Recipient Email"
            name="email"
            type="email"
          />

          <div className="button-prev-next">
            <Button
              disabled={!!Object.keys(errors).length || isProcessing}
              kind={ButtonKind.CUSTOMIZED}
              className="button-continue"
              type="submit"
            >
              {isProcessing ? <Spinner visible={true} /> : "Continue"}
            </Button>
            <Button kind={ButtonKind.CUSTOMIZED} className="button-previous" type="button" click={goBack}>
              Previous
            </Button>
          </div>
        </RecaptchaForm>

        <CoastAlertDisplayError message={formError} isDisplayed={!!formError} />

        <Button kind={ButtonKind.CUSTOMIZED} className="button-cancel" click={onCancel}>
          Cancel
        </Button>
      </section>
    );
  },
);
